import { isNativePlatform, getPlatform, isAmazonDevice, resolveAppUrl, openExternalUrl, watchExternalWindowClose } from '@/utils/capacitor';
import { App } from '@capacitor/app';
import userStore from '@/store/user.store';
import { reloadProfile } from '@/store/app.events';

// Stripe Price IDs (Used on Web)
export const STRIPE_PRICE_ID_MUSICIAN_MONTHLY = process.env.VUE_APP_STRIPE_PRICE_ID_MUSICIAN_MONTHLY || 'price_1SzjS8RtWdq4HY8Cw9R9f0wd';
export const STRIPE_PRICE_ID_MUSICIAN_YEARLY = process.env.VUE_APP_STRIPE_PRICE_ID_MUSICIAN_YEARLY || 'price_1Szja7RtWdq4HY8CUd8KWwTn';

export const STRIPE_PRICE_ID_BACKSTAGE_MONTHLY = process.env.VUE_APP_STRIPE_PRICE_ID_BACKSTAGE_MONTHLY || 'price_1SzjUpRtWdq4HY8Cl4m4ABkm';
export const STRIPE_PRICE_ID_BACKSTAGE_YEARLY = process.env.VUE_APP_STRIPE_PRICE_ID_BACKSTAGE_YEARLY || 'price_1SzjbfRtWdq4HY8CwyZVHdNX';

export const STRIPE_PRICE_ID_CREDITS_10 = process.env.VUE_APP_STRIPE_PRICE_ID_CREDITS_10 || 'price_1SylHARtWdq4HY8CKge824kl';
export const STRIPE_PRICE_ID_CREDITS_25 = process.env.VUE_APP_STRIPE_PRICE_ID_CREDITS_25 || 'price_1SylI4RtWdq4HY8CrgMJPIPo';
export const STRIPE_PRICE_ID_CREDITS_50 = process.env.VUE_APP_STRIPE_PRICE_ID_CREDITS_50 || 'price_1SylK0RtWdq4HY8C3Vc1FWYe';

// Native In-App Purchase Product Identifiers (Apple StoreKit & Google Play Store)
export const PRODUCT_ID_MUSICIAN_MONTHLY = 'app.triadmusic.live.musician.monthly';
export const PRODUCT_ID_MUSICIAN_YEARLY = 'app.triadmusic.live.musician.yearly';
export const PRODUCT_ID_BACKSTAGE_MONTHLY = 'app.triadmusic.live.backstage.monthly';
export const PRODUCT_ID_BACKSTAGE_YEARLY = 'app.triadmusic.live.backstage.yearly';

export const PRODUCT_ID_CREDITS_10 = 'app.triadmusic.live.credits.10';
export const PRODUCT_ID_CREDITS_25 = 'app.triadmusic.live.credits.25';
export const PRODUCT_ID_CREDITS_50 = 'app.triadmusic.live.credits.50';

// Entitlement Identifiers configured in RevenueCat dashboard
export const ENTITLEMENT_MUSICIAN = 'musician_tier';
export const ENTITLEMENT_BACKSTAGE = 'backstage_tier';

// Map between Stripe web price IDs and Native Store Product IDs
export const STRIPE_TO_STORE_PRODUCT_MAP = {
    [STRIPE_PRICE_ID_MUSICIAN_MONTHLY]: PRODUCT_ID_MUSICIAN_MONTHLY,
    [STRIPE_PRICE_ID_MUSICIAN_YEARLY]: PRODUCT_ID_MUSICIAN_YEARLY,
    [STRIPE_PRICE_ID_BACKSTAGE_MONTHLY]: PRODUCT_ID_BACKSTAGE_MONTHLY,
    [STRIPE_PRICE_ID_BACKSTAGE_YEARLY]: PRODUCT_ID_BACKSTAGE_YEARLY,
    [STRIPE_PRICE_ID_CREDITS_10]: PRODUCT_ID_CREDITS_10,
    [STRIPE_PRICE_ID_CREDITS_25]: PRODUCT_ID_CREDITS_25,
    [STRIPE_PRICE_ID_CREDITS_50]: PRODUCT_ID_CREDITS_50,
};

let isRevenueCatConfigured = false;
let initRevenueCatPromise = null;
let configuredUserId = null;
let isSyncing = false;

/**
 * Retrieves valid RevenueCat public API key for current native platform.
 * Returns empty string if missing or set to placeholder.
 */
export const getRevenueCatApiKey = () => {
    const platform = getPlatform();
    const runtimeConfig = typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__ : null;
    let key = '';
    if (platform === 'ios') {
        key = runtimeConfig?.VUE_APP_REVENUECAT_APPLE_KEY || process.env.VUE_APP_REVENUECAT_APPLE_KEY || '';
    } else {
        key = runtimeConfig?.VUE_APP_REVENUECAT_GOOGLE_KEY || process.env.VUE_APP_REVENUECAT_GOOGLE_KEY || '';
    }
    if (!key || key.includes('placeholder') || key.includes('...') || key.trim().length < 15) {
        return '';
    }
    return key.trim();
};

/**
 * Checks whether a valid RevenueCat API key is defined for the current platform.
 */
export const hasRevenueCatKey = () => Boolean(getRevenueCatApiKey());

export const isSubscriptionEnabled = () => {
    const runtimeConfig = typeof window !== 'undefined' ? window.__RUNTIME_CONFIG__ : null;
    const disabledVal = runtimeConfig?.VUE_APP_SUBSCRIPTION_DISABLED ?? process.env.VUE_APP_SUBSCRIPTION_DISABLED;
    if (disabledVal === 'true' || disabledVal === true || disabledVal === '1' || disabledVal === 1) {
        return false;
    }
    return true;
};

export const getSubscriptionProvider = () => {
    return userStore.state.userInfo?.subscriptionProvider || 
           userStore.state.userInfo?.subscription_provider || 
           null;
};

export const isIapSubscription = () => {
    const provider = getSubscriptionProvider();
    return provider === 'revenuecat_play_store' || provider === 'revenuecat_app_store';
};

/**
 * Checks whether the current user has an active Stripe subscription (web checkout or Amazon Fire tablet).
 */
export const isStripeSubscription = () => {
    const provider = getSubscriptionProvider();
    if (provider === 'stripe') return true;
    if (provider === 'revenuecat_play_store' || provider === 'revenuecat_app_store') return false;

    const tier = userStore.state.userInfo?.membershipTier || userStore.state.userInfo?.membership_tier;
    const isFree = !tier || tier === 'free_membership';
    if (isFree) return false;

    // Se estiver em app nativo (iOS/Android não Amazon), é IAP por padrão
    if (isNativePlatform() && !isAmazonDevice()) return false;

    // Amazon Fire tablet com active subscription é Stripe
    if (isAmazonDevice()) return true;

    // Web platform com active subscription: só é Stripe se NÃO for IAP
    if (!isNativePlatform() && !isIapSubscription()) return true;

    return false;
};

/**
 * Initializes RevenueCat SDK on iOS and Android with platform-specific API key.
 * Safely no-ops in Web environment or if API key is not configured.
 */
export const initRevenueCat = async (userId = null) => {
    if (!isNativePlatform()) return;

    const apiKey = getRevenueCatApiKey();
    if (!apiKey) {
        console.warn(`[RevenueCat] Missing or placeholder API key for platform "${getPlatform()}". Native in-app purchases disabled.`);
        return;
    }

    const effectiveUserId = userId || userStore.state.userInfo?.id || null;

    if (isRevenueCatConfigured) {
        if (effectiveUserId && effectiveUserId !== configuredUserId) {
            configuredUserId = effectiveUserId;
            try {
                const { Purchases } = await import('@revenuecat/purchases-capacitor');
                const loginResult = await Purchases.logIn({ appUserID: String(effectiveUserId) });
                if (loginResult?.customerInfo) {
                    await subscriptionService.syncNativeSubscription(loginResult.customerInfo);
                }
            } catch (err) {
                console.warn('[RevenueCat] Purchases.logIn warning:', err);
            }
        }
        return;
    }

    if (initRevenueCatPromise) {
        return initRevenueCatPromise;
    }

    initRevenueCatPromise = (async () => {
        try {
            const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor');

            await Purchases.setLogLevel({ level: LOG_LEVEL.INFO });
            await Purchases.configure({
                apiKey,
                appUserID: effectiveUserId ? String(effectiveUserId) : undefined
            });

            isRevenueCatConfigured = true;
            configuredUserId = effectiveUserId;
            console.log(`[RevenueCat] Configured successfully for platform: ${getPlatform()}`);

            // Listen for subscription status changes in real-time (cancellation, renewal, expiration)
            Purchases.addCustomerInfoUpdateListener((customerInfo) => {
                console.log('[RevenueCat] CustomerInfo update event received');
                subscriptionService.syncNativeSubscription(customerInfo);
            });

            // Recheck customer info when app returns from background (e.g. user managing subscription in Play Store)
            App.addListener('appStateChange', async (state) => {
                if (state.isActive) {
                    try {
                        const info = await Purchases.getCustomerInfo();
                        if (info) {
                            await subscriptionService.syncNativeSubscription(info);
                        }
                    } catch (err) {
                        console.warn('[RevenueCat] Resume check warning:', err);
                    }
                }
            });

            // Fetch initial customer info to synchronize status on app startup
            try {
                const customerInfo = await Purchases.getCustomerInfo();
                if (customerInfo) {
                    await subscriptionService.syncNativeSubscription(customerInfo);
                }
            } catch (infoErr) {
                console.warn('[RevenueCat] Initial customer info check warning:', infoErr);
            }
        } catch (e) {
            console.error('[RevenueCat] Initialization error:', e);
            initRevenueCatPromise = null;
        }
    })();

    return initRevenueCatPromise;
};

/**
 * Retrieves RevenueCat offerings and packages for inspection, testing, and dynamic UI.
 */
export const getOfferings = async () => {
    if (!isNativePlatform() || !hasRevenueCatKey()) return null;
    await initRevenueCat();
    try {
        const { Purchases } = await import('@revenuecat/purchases-capacitor');
        const offerings = await Purchases.getOfferings();
        console.log('[RevenueCat] getOfferings result:', offerings);
        return offerings;
    } catch (e) {
        console.warn('[RevenueCat] Failed to fetch offerings:', e);
        return null;
    }
};

/**
 * Directly queries StoreKit / Google Play for given product IDs (bypassing offering configuration).
 */
export const getStoreProducts = async (productIds = []) => {
    if (!isNativePlatform() || !hasRevenueCatKey()) return [];
    await initRevenueCat();
    try {
        const { Purchases } = await import('@revenuecat/purchases-capacitor');
        const ids = (productIds && productIds.length > 0) ? productIds : [
            'app.triadmusic.live.musician.monthly',
            'app.triadmusic.live.musician.yearly',
            'app.triadmusic.live.backstage.monthly',
            'app.triadmusic.live.backstage.yearly',
            'app.triadmusic.live.credits.10',
            'app.triadmusic.live.credits.25',
            'app.triadmusic.live.credits.50'
        ];
        const res = await Purchases.getProducts({ productIdentifiers: ids });
        console.log('[RevenueCat] getStoreProducts result:', res?.products);
        return res?.products || [];
    } catch (e) {
        console.warn('[RevenueCat] Failed to get store products:', e);
        return [];
    }
};

/**
 * Executes native purchase via Apple StoreKit or Google Play Billing through RevenueCat.
 */
export const purchaseStoreProduct = async (productId) => {
    if (!isNativePlatform()) {
        throw new Error('In-App Purchases are only supported in mobile applications');
    }

    if (!hasRevenueCatKey()) {
        throw new Error('RevenueCat API key is not configured for this platform');
    }

    // Guarantee that Purchases.configure has finished before calling any SDK method
    await initRevenueCat();

    const { Purchases } = await import('@revenuecat/purchases-capacitor');

    // 1. Try to find the package in offerings (current or all defined offerings)
    let matchedPackage = null;
    try {
        const offerings = await Purchases.getOfferings();
        if (offerings) {
            let candidatePackages = [...(offerings.current?.availablePackages || [])];
            if (offerings.all) {
                Object.values(offerings.all).forEach(off => {
                    if (off && off.availablePackages) {
                        off.availablePackages.forEach(pkg => {
                            if (!candidatePackages.some(cp => cp.identifier === pkg.identifier && cp.product?.identifier === pkg.product?.identifier)) {
                                candidatePackages.push(pkg);
                            }
                        });
                    }
                });
            }

            console.log('[RevenueCat] Candidate packages in offerings:', candidatePackages.map(p => ({
                packageId: p.identifier,
                productId: p.product?.identifier
            })));

            const isMusician = productId.includes('musician');
            const isBackstage = productId.includes('backstage');
            const isMonthly = productId.includes('monthly');
            const isYearly = productId.includes('yearly');
            const cleanTargetId = productId.replace('.live.', '.');

            matchedPackage = candidatePackages.find(p => {
                const prodId = p.product?.identifier || '';
                const cleanProdId = prodId.replace('.live.', '.');
                const pkgId = p.identifier || '';
                const offId = (p.offeringIdentifier || '').toLowerCase();

                // Prevent cross-offering mismatch
                if (isMusician && offId && !offId.includes('musician') && !offId.includes('default')) return false;
                if (isBackstage && offId && !offId.includes('backstage') && !offId.includes('default')) return false;

                // A. Exact or prefix match on product ID (both with and without .live.)
                if (prodId === productId || prodId.startsWith(productId + ':')) {
                    return true;
                }
                if (cleanProdId === cleanTargetId || cleanProdId.startsWith(cleanTargetId + ':')) {
                    return true;
                }

                // B. Exact package identifier match
                if (pkgId === productId || pkgId === cleanTargetId) {
                    return true;
                }

                // C. Explicit custom package identifier match
                if (isMusician && isMonthly && (pkgId === 'musician_monthly' || pkgId === 'musician-monthly' || pkgId === 'musician_month')) return true;
                if (isMusician && isYearly && (pkgId === 'musician_yearly' || pkgId === 'musician-yearly' || pkgId === 'musician_annual')) return true;
                if (isBackstage && isMonthly && (pkgId === 'backstage_monthly' || pkgId === 'backstage-monthly' || pkgId === 'backstage_month')) return true;
                if (isBackstage && isYearly && (pkgId === 'backstage_yearly' || pkgId === 'backstage-yearly' || pkgId === 'backstage_annual')) return true;

                // D. Standard RC packages ($rc_monthly / $rc_annual / monthly / annual)
                if (isMusician && !prodId.includes('backstage') && !pkgId.includes('backstage')) {
                    if (offId.includes('musician') || offId.includes('default') || !offId) {
                        if (isMonthly && (pkgId === '$monthly' || pkgId === '$rc_monthly' || pkgId === 'monthly')) return true;
                        if (isYearly && (pkgId === '$annual' || pkgId === '$rc_annual' || pkgId === 'yearly' || pkgId === 'annual')) return true;
                    }
                }
                if (isBackstage && !prodId.includes('musician') && !pkgId.includes('musician')) {
                    if (offId.includes('backstage') || offId.includes('default') || !offId) {
                        if (isMonthly && (pkgId === '$monthly' || pkgId === '$rc_monthly' || pkgId === 'monthly')) return true;
                        if (isYearly && (pkgId === '$annual' || pkgId === '$rc_annual' || pkgId === 'yearly' || pkgId === 'annual')) return true;
                    }
                }

                return false;
            });
        }
    } catch (e) {
        console.warn('[RevenueCat] Offerings lookup warning:', e);
    }

    // 2. If a package was found, purchase it directly. If user cancels or it errors, THROW IMMEDIATELY! Do NOT fall through!
    if (matchedPackage) {
        console.log('[RevenueCat] Purchasing matched package:', matchedPackage.identifier, matchedPackage.product?.identifier);
        const result = await Purchases.purchasePackage({ aPackage: matchedPackage });
        return result.customerInfo;
    }

    // 3. Fallback: Only if NO package was found in offerings, try direct store product
    console.log('[RevenueCat] No package matched in offerings, querying store products for fallback:', productId);
    const baseWithoutLive = productId.replace('.live.', '.');
    const baseWithLive = productId.includes('.live.') ? productId : productId.replace('app.triadmusic.', 'app.triadmusic.live.');
    const possibleIds = Array.from(new Set([
        productId,
        baseWithoutLive,
        baseWithLive,
        `${productId}:monthly`,
        `${productId}:yearly`,
        `${baseWithoutLive}:monthly`,
        `${baseWithoutLive}:yearly`,
        `${baseWithLive}:monthly`,
        `${baseWithLive}:yearly`
    ]));

    const { products } = await Purchases.getProducts({ productIdentifiers: possibleIds });
    if (products && products.length > 0) {
        // Pick the product that strictly matches the requested cycle
        const targetProduct = products.find(p => {
            const id = p.identifier || '';
            if (productId.includes('monthly') && id.includes('monthly')) return true;
            if (productId.includes('yearly') && id.includes('yearly')) return true;
            return false;
        }) || products[0];

        console.log('[RevenueCat] Purchasing direct store product:', targetProduct.identifier);
        const result = await Purchases.purchaseStoreProduct({ product: targetProduct });
        return result.customerInfo;
    }

    throw new Error(`Product ${productId} not found in store offerings.`);
};

/**
 * Restores previous purchases made on the Apple ID or Google Play account.
 * (Mandatory Apple App Store requirement under Guideline 3.1.1).
 */
export const restorePurchases = async () => {
    if (!isNativePlatform()) {
        throw new Error('Restore purchases is only available on native mobile platforms');
    }

    if (!hasRevenueCatKey()) {
        throw new Error('RevenueCat API key is not configured for this platform');
    }

    // Guarantee that Purchases.configure has finished before calling any SDK method
    await initRevenueCat();

    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const result = await Purchases.restorePurchases();
    
    // Sync restored entitlement status with backend
    if (result && result.customerInfo) {
        await subscriptionService.syncNativeSubscription(result.customerInfo);
    }

    return result.customerInfo;
};

/**
 * Directs user to native subscription management (Apple App Store / Google Play / Stripe Customer Portal).
 */
export const manageSubscriptions = async () => {
    const provider = getSubscriptionProvider();

    // 1. Google Play IAP (seja no app mobile ou na Web)
    if (provider === 'revenuecat_play_store') {
        const url = 'https://play.google.com/store/account/subscriptions';
        if (isNativePlatform()) {
            await openExternalUrl(url);
        } else {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
        return;
    }

    // 2. Apple App Store IAP (seja no app mobile ou na Web)
    if (provider === 'revenuecat_app_store') {
        const url = 'https://apps.apple.com/account/subscriptions';
        if (isNativePlatform()) {
            await openExternalUrl(url);
        } else {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
        return;
    }

    // 3. Fallback nativo se a plataforma for iOS/Android (não Amazon)
    if (isNativePlatform() && !isAmazonDevice()) {
        const platform = getPlatform();
        const url = platform === 'ios'
            ? 'https://apps.apple.com/account/subscriptions'
            : 'https://play.google.com/store/account/subscriptions';
        await openExternalUrl(url);
        return;
    }

    // 4. Stripe Customer Portal (Web e Amazon Fire Tablet)
    let popupWindow = null;
    if (!isNativePlatform()) {
        try {
            popupWindow = window.open('about:blank', '_blank');
        } catch (popupErr) {
            console.warn('Could not pre-open blank window:', popupErr);
        }
    }

    const onPortalClosed = async () => {
        console.log('[manageSubscriptions] Stripe Portal window closed by user, refreshing profile...');
        try {
            await userStore.dispatch('info', true);
            reloadProfile();
        } catch (err) {
            console.warn('Error reloading user info after portal closed:', err);
        }
    };

    try {
        const portal = await subscriptionService.createPortalSession();
        if (portal && portal.url) {
            if (isNativePlatform()) {
                // Amazon Fire tablet / Native: open in device's browser
                watchExternalWindowClose(null, onPortalClosed);
                await openExternalUrl(portal.url);
            } else if (popupWindow && !popupWindow.closed) {
                // Web or Web PWA: open in browser using _blank page
                watchExternalWindowClose(popupWindow, onPortalClosed);
                popupWindow.location.href = portal.url;
                popupWindow.focus();
            } else {
                const openedWin = window.open(portal.url, '_blank', 'noopener,noreferrer');
                watchExternalWindowClose(openedWin, onPortalClosed);
            }
        } else if (popupWindow && !popupWindow.closed) {
            popupWindow.close();
        }
        return portal;
    } catch (e) {
        if (popupWindow && !popupWindow.closed) {
            popupWindow.close();
        }
        throw e;
    }
};

export const subscriptionService = {
    /**
     * Unified subscription entry point.
     * Web & Amazon Fire Tablet -> Stripe Checkout Session.
     * Native iOS/Android -> Apple StoreKit / Google Play via RevenueCat (if key configured).
     */
    async subscribe(priceId, returnUrl) {
        if (!isNativePlatform() || isAmazonDevice()) {
            return await this.createCheckoutSession(priceId, returnUrl);
        }

        if (!hasRevenueCatKey()) {
            throw new Error('As assinaturas no aplicativo ainda não estão configuradas neste ambiente.');
        }

        try {
            // Native Mobile com RevenueCat configurado (Google Play / App Store)
            const storeProductId = STRIPE_TO_STORE_PRODUCT_MAP[priceId] || priceId;
            const customerInfo = await purchaseStoreProduct(storeProductId);

            // Sync entitlement with backend
            await this.syncNativeSubscription(customerInfo);

            return {
                native: true,
                customerInfo
            };
        } catch (error) {
            const errorMsg = String(error?.message || error || '').toLowerCase();
            const isStoreUnavailable = 
                errorMsg.includes('not allowed') || 
                errorMsg.includes('not_allowed') ||
                errorMsg.includes('billing_unavailable') ||
                errorMsg.includes('feature_not_supported') ||
                errorMsg.includes('service_unavailable') ||
                errorMsg.includes('play store') ||
                errorMsg.includes('store is not available') ||
                errorMsg.includes('purchases are not allowed') ||
                errorMsg.includes('item_unavailable') ||
                errorMsg.includes('billing unavailable') ||
                errorMsg.includes('cannot connect');

            // Compliance com Apple App Store & Google Play: NUNCA redirecionar para Stripe na App Store / Play Store.
            // Permitir fallback para Stripe Web Checkout apenas se o dispositivo for comprovadamente um Amazon Fire Tablet.
            if (isStoreUnavailable && isAmazonDevice()) {
                console.warn('[Subscription] Loja nativa indisponível em dispositivo Amazon Fire. Usando Stripe Web Checkout...', error);
                return await this.createCheckoutSession(priceId, returnUrl);
            }

            throw error;
        }
    },

    async createCheckoutSession(priceId, returnUrl) {
        const response = await fetch('/api/user/checkout-session', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                priceId,
                returnUrl: returnUrl || window.location.href
            })
        });
        const data = await response.json();
        if (!response.ok || data.error) {
            throw new Error(data.message || data.error || 'Failed to create checkout session');
        }
        return data;
    },

    async createPortalSession() {
        const response = await fetch('/api/user/portal-session', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        });
        const data = await response.json();
        if (!response.ok || data.error) {
            throw new Error(data.message || data.error || 'Failed to create portal session');
        }
        return data;
    },

    async purchaseCredits(priceId, returnUrl) {
        if (!isNativePlatform()) {
            const response = await fetch('/api/user/purchase-credits', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    priceId,
                    returnUrl: returnUrl || window.location.href
                })
            });
            const data = await response.json();
            if (!response.ok || data.error) {
                throw new Error(data.message || data.error || 'Failed to purchase credits');
            }
            return data;
        }

        if (!hasRevenueCatKey()) {
            throw new Error('As compras de créditos no aplicativo ainda não estão configuradas neste ambiente.');
        }

        // Native In-App Purchase for consumable credits
        const storeProductId = STRIPE_TO_STORE_PRODUCT_MAP[priceId] || priceId;
        const customerInfo = await purchaseStoreProduct(storeProductId);
        await this.syncNativeSubscription(customerInfo);
        return { native: true, customerInfo };
    },

    async cancelSubscription(options = {}) {
        if (isIapSubscription() || (isNativePlatform() && !isStripeSubscription())) {
            await manageSubscriptions();
            return { native: true, redirected: true };
        }

        const cancelUrl = resolveAppUrl('/api/user/cancel-subscription');
        const response = await fetch(cancelUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(options)
        });
        const data = await response.json();
        if (!response.ok || data.error) {
            throw new Error(data.message || data.error || 'Failed to cancel subscription');
        }
        return data;
    },

    isStripeSubscription() {
        return isStripeSubscription();
    },

    isIapSubscription() {
        return isIapSubscription();
    },

    getSubscriptionProvider() {
        return getSubscriptionProvider();
    },

    async restorePurchases() {
        return await restorePurchases();
    },

    async getOfferings() {
        return await getOfferings();
    },

    async getStoreProducts(productIds) {
        return await getStoreProducts(productIds);
    },

    /**
     * Synchronizes RevenueCat customer entitlements with Mytriad backend.
     */
    async syncNativeSubscription(customerInfo) {
        if (isSyncing) return;
        isSyncing = true;

        try {
            const activeEntitlements = customerInfo?.entitlements?.active || {};
            let hasActiveIAP = false;
            let tier = 'free_membership';
            let activeEntitlement = null;
            if (activeEntitlements[ENTITLEMENT_BACKSTAGE] || activeEntitlements['backstage']) {
                tier = 'backstage_tier';
                hasActiveIAP = true;
                activeEntitlement = activeEntitlements[ENTITLEMENT_BACKSTAGE] || activeEntitlements['backstage'];
            } else if (activeEntitlements[ENTITLEMENT_MUSICIAN] || activeEntitlements['musician']) {
                tier = 'musician_tier';
                hasActiveIAP = true;
                activeEntitlement = activeEntitlements[ENTITLEMENT_MUSICIAN] || activeEntitlements['musician'];
            }

            const cancelAtPeriodEnd = hasActiveIAP && activeEntitlement ? (activeEntitlement.willRenew === false) : false;
            const platform = getPlatform();
            const provider = platform === 'ios' ? 'revenuecat_app_store' : 'revenuecat_play_store';

            // Atualiza o estado imediatamente na UI (ativo, cancelamento agendado ou expirado para free)
            if (userStore.state.userInfo) {
                const targetTier = hasActiveIAP ? tier : 'free_membership';
                const targetProvider = hasActiveIAP ? provider : null;
                userStore.commit('INFO', {
                    ...userStore.state.userInfo,
                    membershipTier: targetTier,
                    membership_tier: targetTier,
                    subscriptionProvider: targetProvider,
                    subscription_provider: targetProvider,
                    cancelAtPeriodEnd
                });
                try {
                    localStorage.setItem('triad_offline_user', JSON.stringify({
                        ...userStore.state.userInfo,
                        membershipTier: targetTier,
                        membership_tier: targetTier,
                        subscriptionProvider: targetProvider,
                        subscription_provider: targetProvider,
                        cancelAtPeriodEnd
                    }));
                } catch (storageErr) {
                    // Ignore storage quota
                }
            }

            const syncUrl = resolveAppUrl('/api/user/sync-iap');
            await fetch(syncUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    tier: hasActiveIAP ? tier : 'free_membership',
                    provider: hasActiveIAP ? provider : null,
                    entitlements: activeEntitlements,
                    originalAppUserId: customerInfo?.originalAppUserId,
                    cancelAtPeriodEnd
                })
            });

            // Atualiza os dados consolidados do backend (fonte da verdade)
            await userStore.dispatch('updateBasicInfo');
        } catch (e) {
            console.warn('[RevenueCat] Backend sync warning:', e);
        } finally {
            isSyncing = false;
        }
    }
};

export default subscriptionService;
